import {
    AutoComplete,
    Button,
    Cascader,
    Checkbox,
    Form,
    Input,
    InputNumber,
    Radio,
    Select,
    Space,
    Tooltip,
    type FormInstance
} from "antd";
import { type ChangeEvent, type ReactNode, type RefObject, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import {
    Button as ZhButton,
    DetailHeader as ZhDetailHeader,
    Dialog as ZhDialog,
    DatePicker as ZhDatePicker,
    Grid as ZhGrid,
    GridItem as ZhGridItem
} from "zhihao-ui-react";
import type { MapViewForm } from "@zhihaoscm/entity";
import { FormTypeEnum } from "@zhihaoscm/meta";

import request from "@/api";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { consignmentService } from "@/api/services/consignmentService";
import { platBankAccountService } from "@/api/services/platBankAccountService";
import { prodTypeService } from "@/api/services/prodTypeService";
import WangEditor, { type RichEditorRef } from "@/views/components/WangEditor";
import ZhMapDialog from "@/views/components/zhMapDialog";
import {
    loadOilSiteAddressSuggestions,
    loadOilSiteAreaOptions,
    type OilSiteAddressSuggestion,
    type OilSiteAreaOption
} from "@/views/pages/logistics/oil/OilSiteFormPage";
import { sliceGbCode } from "@/views/pages/logistics/wharf";
import type { ConsignmentFormValues, ConsignmentRow } from "./types";
import "./ConsignmentFormPage.scss";

export interface ConsignmentFormPageProps {
    loadConsignment?: (id: number | string) => Promise<ConsignmentRow>;
    saveConsignment?: (id: number | string | undefined, values: ConsignmentFormValues) => Promise<void>;
    submitConsignment?: (id: number | string | undefined, values: ConsignmentFormValues) => Promise<void>;
    loadAddressSuggestions?: (keyword: string, regionCode?: string) => Promise<OilSiteAddressSuggestion[]>;
}

interface ConsignmentUploadedFile {
    id?: number;
    path?: string;
}

export interface ProductMediaFile {
    id: number;
    path?: string;
}

interface BankAccountValue {
    name?: string;
    account?: string;
    openingBank?: string;
    isOwnAccount?: number;
    supportOnline?: number;
}

const CONSIGNMENT_IMAGE_TYPES = ["image/jpeg", "image/png"];
const CONSIGNMENT_VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/x-matroska"];
const CONSIGNMENT_IMAGE_MAX_SIZE = 2 * 1024 * 1024;
const CONSIGNMENT_VIDEO_MAX_SIZE = 200 * 1024 * 1024;

export const DEFAULT_PRODUCT_PARAMETERS: ParameterItem[] = ["含泥量", "压碎值", "石粉MB值", "含水率", "细粉含量"].map(
    (parameterName, displayOrder) => ({ displayOrder, parameterName, parameterValue: "" })
);
export const DEFAULT_SETTLEMENT_DESCN =
    "我们承诺提供透明的结算流程和公正的交易环境，确保每一位用户的权益得到保护。如有任何疑问或需要进一步的帮助，欢迎随时联系我们的客服团队。";
export const DEFAULT_SERVICE_GUARANTEE =
    "本平台致力于提供高质量的商品信息和交易服务。我们承诺所发布的商品信息真实可靠，供应商资质经过严格审核。我们的目标是通过提供详细的商品介绍、供应商信息和提货指南，确保用户能够顺利完成交易。我们鼓励用户在交易过程中保持沟通，以便及时解决可能出现的问题。我们承诺对用户的反馈和建议给予高度重视，并不断优化我们的服务流程。";
export const DEFAULT_CONSIGNMENT_REGULATION =
    "申请样品规则：\n1、同一用户对同一件寄售商品，仅能申请 1 次样品，不可重复申请。​\n2、样品本身免费，但运费需由用户自行支付。​\n3、样品发出后，系统会自动发送短信至用户注册的手机号，告知发货信息​。";

type ParameterItem = NonNullable<ConsignmentFormValues["parameterInfo"]>[number];
type SelectOption = { label: string; value: number | string };
type ProductSelectOption = SelectOption & { specificationModulus?: string; tags?: string[] };

const PRICE_TYPE_TIPS: Record<number, string> = {
    1: "固定价格：为实际交易价格，不受市场行情影响",
    2: "预估价格：价格可能存在变动，以提货当日价格为准"
};

/** 复刻 Vue icon-a-Frame38 的价格说明图标与暗色提示。 */
function PriceTypeTip({ type }: { type: 1 | 2 }) {
    return (
        <Tooltip title={PRICE_TYPE_TIPS[type]} placement="top">
            <button
                type="button"
                className="consignment-form__price-tip"
                aria-label={`${type === 1 ? "固定" : "预估"}价格说明`}
            >
                <svg viewBox="0 0 18 18" aria-hidden="true">
                    <circle cx="9" cy="9" r="7" />
                    <path d="M9 8.2v4.2M9 5.4h.01" />
                </svg>
            </button>
        </Tooltip>
    );
}

/** 兼容旧测试和迁移数据，把 JSON 文本安全还原为参数数组。 */
function parseParameterText(value?: string): ParameterItem[] {
    try {
        const result: unknown = JSON.parse(value || "[]");
        return Array.isArray(result) ? (result as ParameterItem[]) : [];
    } catch {
        return [];
    }
}

/** Vue section-card 的 React 结构，统一章节标题和卡片间距。 */
export function FormSection({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="section-card consignment-form__section">
            <h2 className="section-title">{title}</h2>
            {children}
        </section>
    );
}

/** 还原 Vue upload-list 的上传入口、说明文本和媒体回显。 */
export function MediaUploadCard({
    type,
    path,
    onChange,
    inputIdPrefix = "consignment",
    inputAriaLabel,
    statusText
}: {
    type: "image" | "video";
    path: string | null;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    inputIdPrefix?: string;
    inputAriaLabel?: string;
    statusText?: string;
}) {
    const isImage = type === "image";
    const inputId = `${inputIdPrefix}-${type}-upload`;
    return (
        <div className={`consignment-upload consignment-upload--${type}`}>
            <p className="consignment-upload__hint">
                {isImage
                    ? "图片上传受限制，仅支持JPEG、PNG格式，单张2MB，尺寸不超过800*800像素，请勿包含敏感信息或侵权内容"
                    : "仅支持mov,mp4格式，大小不超过200M"}
            </p>
            <button
                type="button"
                className="consignment-upload__card"
                aria-label={isImage ? "上传图片" : "上传视频"}
                onClick={() => document.getElementById(inputId)?.click()}
            >
                {isImage && path ? (
                    <img src={path} alt="寄售商品主图" />
                ) : (
                    <span className="consignment-upload__icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" fill="none">
                            <path
                                d="M8 17H6.5a4.5 4.5 0 0 1-.49-8.97A6 6 0 0 1 17.5 9.5h.5a3.5 3.5 0 0 1 0 7h-2"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                            />
                            <path
                                d="m9 13 3-3 3 3m-3-3v10"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </span>
                )}
                <span>{isImage ? "上传图片" : "上传视频"}</span>
            </button>
            <input
                id={inputId}
                className="consignment-form__legacy-control"
                aria-label={inputAriaLabel || (isImage ? "上传寄售商品主图" : "上传寄售商品视频")}
                accept={isImage ? "image/jpeg,image/png" : "video/mp4,video/quicktime,video/x-matroska"}
                type="file"
                onChange={onChange}
            />
            {path || statusText ? (
                <span className="consignment-form__legacy-status">
                    {isImage ? "主图已上传：" : "视频已上传："}
                    {statusText || path}
                </span>
            ) : null}
        </div>
    );
}

/** 旧 UploadList 的可回显列表：第一张为主图，其余图片进入 imgIds，最多六张。 */
export function ProductMediaUpload({
    type,
    files,
    onUpload,
    onRemove,
    onSetMain,
    inputIdPrefix
}: {
    type: "image" | "video";
    files: ProductMediaFile[];
    onUpload: (event: ChangeEvent<HTMLInputElement>, replaceIndex?: number) => void;
    onRemove: (index: number) => void;
    onSetMain?: (index: number) => void;
    inputIdPrefix: string;
}) {
    const isImage = type === "image";
    const maxLength = isImage ? 6 : 1;
    const inputId = `${inputIdPrefix}-${type}-upload`;
    const replaceIndexRef = useRef<number | undefined>(undefined);
    const [previewFile, setPreviewFile] = useState<ProductMediaFile>();
    return (
        <div className={`consignment-upload consignment-upload--${type}`}>
            <p className="consignment-upload__hint">
                {isImage
                    ? "图片上传受限制，仅支持JPEG、PNG格式，单张2MB，尺寸不超过800*800像素，请勿包含敏感信息或侵权内容"
                    : "仅支持mov,mp4格式，大小不超过200M"}
            </p>
            <div className="consignment-upload__list" data-testid={`${inputIdPrefix}-${type}-list`}>
                {/* Vue UploadList keeps the add card before existing files and uses the 232×122 media size. */}
                {files.length < maxLength ? (
                    <button
                        aria-label={isImage ? "上传图片" : "上传视频"}
                        className="consignment-upload__card"
                        type="button"
                        onClick={() => document.getElementById(inputId)?.click()}
                    >
                        <span className="consignment-upload__icon" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M8 17H6.5a4.5 4.5 0 0 1-.49-8.97A6 6 0 0 1 17.5 9.5h.5a3.5 3.5 0 0 1 0 7h-2"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                />
                                <path
                                    d="m9 13 3-3 3 3m-3-3v10"
                                    stroke="currentColor"
                                    strokeWidth="1.6"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                        <span>{isImage ? "上传图片" : "上传视频"}</span>
                    </button>
                ) : null}
                {files.map((file, index) => (
                    <div className="consignment-upload__file" key={file.id}>
                        {isImage ? (
                            <button
                                aria-label={`预览商品图片 ${index + 1}`}
                                className="block h-full w-full"
                                type="button"
                                onClick={() => setPreviewFile(file)}
                            >
                                <img alt={`商品图片 ${index + 1}`} src={file.path || String(file.id)} />
                            </button>
                        ) : file.path ? (
                            <video aria-label={`商品视频 ${index + 1}`} muted src={file.path} />
                        ) : (
                            <span>视频 {file.id}</span>
                        )}
                        {isImage && index > 0 ? (
                            <button
                                aria-label={`设置商品图片 ${index + 1} 为主图`}
                                className="consignment-upload__file-action consignment-upload__file-action--main"
                                type="button"
                                onClick={() => onSetMain?.(index)}
                            >
                                设置主图
                            </button>
                        ) : null}
                        <button
                            aria-label={`替换商品${isImage ? "图片" : "视频"} ${index + 1}`}
                            className="consignment-upload__file-action consignment-upload__file-action--replace"
                            type="button"
                            onClick={() => {
                                replaceIndexRef.current = index;
                                document.getElementById(inputId)?.click();
                            }}
                        >
                            替换
                        </button>
                        <button
                            aria-label={`${isImage ? "删除商品图片" : "删除商品视频"} ${index + 1}`}
                            className="consignment-upload__file-remove"
                            type="button"
                            onClick={() => onRemove(index)}
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>
            <input
                id={inputId}
                aria-label={
                    inputIdPrefix === "consignment"
                        ? isImage
                            ? "上传寄售商品主图"
                            : "上传寄售商品视频"
                        : isImage
                          ? "主图上传"
                          : "视频上传"
                }
                className="consignment-form__legacy-control"
                accept={isImage ? "image/jpeg,image/png" : "video/mp4,video/quicktime,video/x-matroska"}
                multiple={isImage}
                type="file"
                onChange={event => {
                    onUpload(event, replaceIndexRef.current);
                    replaceIndexRef.current = undefined;
                }}
            />
            {files[0] ? (
                <span className="consignment-form__legacy-status">
                    {isImage ? "主图已上传：" : "视频已上传："}
                    {inputIdPrefix === "consignment" ? files[0].path || files[0].id : files[0].id}
                </span>
            ) : null}
            <ZhDialog
                open={Boolean(previewFile)}
                title="预览图片"
                width={720}
                onCancel={() => setPreviewFile(undefined)}
                footer={
                    <ZhButton aria-label="关闭预览图片" onClick={() => setPreviewFile(undefined)}>
                        关闭
                    </ZhButton>
                }
            >
                {previewFile ? (
                    <img
                        alt="预览商品图片"
                        className="max-h-[70vh] w-full object-contain"
                        src={previewFile.path || String(previewFile.id)}
                    />
                ) : null}
            </ZhDialog>
        </div>
    );
}

/** 商品专员电话按旧版最多三项维护，正式提交由表单规则统一校验。 */
export function HandlerMobileEditor({
    value = [""],
    onChange
}: {
    value?: string[];
    onChange?: (value: string[]) => void;
}) {
    const items = value.length ? value : [""];
    return (
        <div className="grid grid-cols-3 gap-6">
            {items.map((item, index) => (
                <div className="flex items-start gap-2" key={index}>
                    <Input
                        aria-label={`商品专员联系电话 ${index + 1}`}
                        maxLength={18}
                        placeholder="请输入商品专员联系电话"
                        value={item}
                        onChange={event => {
                            const next = [...items];
                            next[index] = event.target.value.replace(/\s/g, "");
                            onChange?.(next);
                        }}
                    />
                    {items.length > 1 ? (
                        <Button
                            aria-label={`删除联系电话 ${index + 1}`}
                            onClick={() => onChange?.(items.filter((_, i) => i !== index))}
                        >
                            ×
                        </Button>
                    ) : null}
                    {index === items.length - 1 && items.length < 3 ? (
                        <Button aria-label="新增联系电话" onClick={() => onChange?.([...items, ""])}>
                            +新增联系电话
                        </Button>
                    ) : null}
                </div>
            ))}
        </div>
    );
}

/** 线下收款账号最多五项，选项保留完整账户对象并阻止重复选择。 */
export function OfflineAccountEditor({
    value = [{}],
    onChange
}: {
    value?: BankAccountValue[];
    onChange?: (value: BankAccountValue[]) => void;
}) {
    const [options, setOptions] = useState<BankAccountValue[]>([]);
    const [duplicateError, setDuplicateError] = useState("");
    const items = value.length ? value : [{}];

    const search = async (keyword = "") => {
        const data = await platBankAccountService.getByName({ state: 1, keyWord: keyword });
        setOptions(data);
    };

    return (
        <div aria-label="线下收款账号" className="flex flex-col gap-3">
            <span className="consignment-form__label consignment-form__label--required">收款账号（线下）</span>
            {items.map((item, index) => (
                <div className="flex items-start gap-4" key={index}>
                    <Select
                        aria-label={`线下收款账号 ${index + 1}`}
                        className="w-1/2"
                        allowClear
                        showSearch
                        filterOption={false}
                        placeholder="请选择收款账户"
                        value={item.account || undefined}
                        options={options.map(account => ({
                            label: `${account.name}/${account.account}/${account.openingBank}`,
                            value: account.account
                        }))}
                        onFocus={() => void search()}
                        onSearch={keyword => void search(keyword)}
                        onChange={accountNo => {
                            if (
                                accountNo &&
                                items.some((entry, itemIndex) => itemIndex !== index && entry.account === accountNo)
                            ) {
                                setDuplicateError("请勿重复添加同一收款账户");
                                return;
                            }
                            const selected = options.find(option => option.account === accountNo) || {};
                            const next = [...items];
                            next[index] = selected;
                            setDuplicateError("");
                            onChange?.(next);
                        }}
                    />
                    {item.account ? <span>是否自有账户：{item.isOwnAccount ? "是" : "否"}</span> : null}
                    {items.length > 1 ? (
                        <Button
                            aria-label={`删除线下收款账号 ${index + 1}`}
                            onClick={() => onChange?.(items.filter((_, i) => i !== index))}
                        >
                            ×
                        </Button>
                    ) : null}
                </div>
            ))}
            {duplicateError ? (
                <span className="text-red-500" role="alert">
                    {duplicateError}
                </span>
            ) : null}
            {items.length < 5 ? (
                <Button type="link" aria-label="新增收款账号" onClick={() => onChange?.([...items, {}])}>
                    + 新增收款账号
                </Button>
            ) : null}
        </div>
    );
}

/**
 * 读取旧 editorManage 的 `==ids=` 头及商品编辑器 URL 尾部的 `#fileId`。
 * 前者用于后端只保留媒体 ID 清单的历史数据，后者用于可逐项识别、删除的正文媒体。
 */
export function extractProductEditorFileIds(content?: string): number[] {
    const result: number[] = [];
    const header = content?.match(/^==ids=(.*?)\n/);
    header?.[1]
        .split("--")
        .map(Number)
        .filter(Boolean)
        .forEach(id => result.push(id));
    const pattern = /<(?:img|video|source)\s+[^>]*src=["'][^"']*#(\d+)["']/gi;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(content || "")) !== null) {
        const id = Number(match[1]);
        if (id && !result.includes(id)) result.push(id);
    }
    return result;
}

export interface LegacyDraftValues {
    title?: string;
    weight?: number;
    unitPrice?: number;
    groupUnitPrice?: number;
    productTypeId?: string;
    productTypeSpec?: string;
    parameterInfo?: ParameterItem[];
    productLabel?: string[];
    handlerId?: number;
    handlerMobile?: string[];
    waitingPayTime?: number;
    deliveryStartTime?: string | null;
    deliveryEndTime?: string | null;
    groupEndTime?: string;
    inventoryQuantity?: number;
    minOrderTon?: number;
    minAdjustmentTon?: number;
    groupSuccessTon?: number;
    floatType?: number;
    groupFloatRatio?: number;
    groupFloatTon?: number;
    isIncludeTax?: number;
    taxRate?: number;
    settlementMethod?: number;
    depositPaymentMethod?: number;
    paymentRatio?: number;
    fixedAmount?: number;
}

/** 保存草稿仍运行旧 Vue 的永久必填、长度与已填写字段校验，仅跳过 required: !isDraft 的规则。 */
export function validateLegacyProductDraft(
    values: LegacyDraftValues,
    kind: "consignment" | "groupPurchase",
    context: { draft?: boolean; editing?: boolean; reservedQuantity?: number } = {}
): string | undefined {
    if ((values.title?.length || 0) > 30) return "不超过30个字符";
    if (values.weight != null && (values.weight < 0 || values.weight > 10000 || !Number.isInteger(values.weight)))
        return "大于等于0，小于等于10000的整数";
    for (const price of [values.unitPrice, values.groupUnitPrice]) {
        if (price != null && (price < 0.01 || price > 10000)) return "大于0，小于等于10000的数，保留2位小数";
    }
    if (!values.productTypeId) return "请选择品类";
    if ((values.productTypeSpec?.length || 0) > 15) return "不超过15个字符";
    if (
        values.parameterInfo?.some(
            item => (item.parameterName?.length || 0) > 10 || (item.parameterValue?.length || 0) > 10
        )
    )
        return "参数名称和参数值不能超过10个字符";
    if (values.productLabel?.some(label => label.length > 6)) return "商品标签不能超过6个字符";
    const enteredMobiles = values.handlerMobile?.filter(Boolean) || [];
    if (enteredMobiles.some(mobile => !/^[0-9-]{1,18}$/.test(mobile))) return "请输入正确的手机号";
    if (context.draft === false && values.isIncludeTax == null) return "请选择是否含税";
    if (context.draft === false && values.isIncludeTax === 1 && !values.taxRate) return "请选择税率";
    if (context.draft === false && kind === "groupPurchase" && values.floatType == null) return "请选择浮动方式";
    if (values.settlementMethod === 2) {
        if (!values.depositPaymentMethod) return "请选择保证金支付方式";
        if (
            values.depositPaymentMethod === 1 &&
            (values.paymentRatio == null || values.paymentRatio < 0 || values.paymentRatio > 99)
        )
            return values.paymentRatio == null ? "请输入货款比例" : "货款比例为大于等于0，小于100的整数";
        if (values.depositPaymentMethod === 2 && (!values.fixedAmount || values.fixedAmount > 999999))
            return values.fixedAmount ? "固定金额为大于0，小于1000000的整数" : "请输入固定金额";
    }
    if (context.draft === false && values.floatType === 1 && values.groupFloatRatio == null) return "请输入浮动比例";
    if (context.draft === false && values.floatType === 2 && values.groupFloatTon == null) return "请输入浮动固定吨数";

    if (values.deliveryStartTime || values.deliveryEndTime) {
        if (!values.deliveryEndTime) return "请选择提货结束日期";
        if (values.deliveryStartTime && values.deliveryStartTime > values.deliveryEndTime)
            return "提货结束日期不能早于提货开始日期";
        const endDay = new Date(`${values.deliveryEndTime}T00:00:00`).getTime();
        const today = new Date().setHours(0, 0, 0, 0);
        if (endDay < today)
            return kind === "groupPurchase" ? "提货结束日期不能早于当前时间" : "提货结束日期不能小于当前日期";
        if (kind === "groupPurchase" && values.groupEndTime) {
            const groupDay = new Date(values.groupEndTime).setHours(0, 0, 0, 0);
            if (groupDay >= endDay) return "提货结束日期不能早于拼团截止日期";
            if (!context.editing && values.deliveryStartTime) {
                const startDay = new Date(`${values.deliveryStartTime}T00:00:00`).getTime();
                if (groupDay >= startDay) return "提货开始日期不能早于拼团截止日期";
            }
        }
    }
    if (values.groupEndTime && new Date(values.groupEndTime).getTime() < Date.now())
        return "拼团截止时间不能选择当前之前的时间";

    if (values.minOrderTon === 0) return kind === "groupPurchase" ? "拼团最低吨数需大于为0" : "起订吨数需大于为0";
    if ((values.minOrderTon || 0) > 10000000)
        return kind === "groupPurchase" ? "拼团最低吨数不能大于10000000吨" : "起订吨数不能大于10000000吨";
    if (
        kind === "groupPurchase" &&
        values.minOrderTon != null &&
        values.groupSuccessTon != null &&
        values.minOrderTon > values.groupSuccessTon
    )
        return "拼团最低吨数不能大于拼团成功吨数";
    if (
        kind === "consignment" &&
        values.minOrderTon != null &&
        values.inventoryQuantity != null &&
        values.minOrderTon > values.inventoryQuantity
    )
        return "起订吨数不能大于库存量";
    if (kind === "consignment") {
        if (values.minAdjustmentTon === 0) return "最小调整吨数需大于为0";
        if (values.minOrderTon && values.minAdjustmentTon == null) return "请输入最小调整吨数";
        if (
            values.minOrderTon != null &&
            values.minAdjustmentTon != null &&
            values.minAdjustmentTon > values.minOrderTon
        )
            return "最小调整吨数不能大于起订吨数";
        if ((values.minAdjustmentTon || 0) > 10000000) return "最小调整吨数不能大于10000000吨";
    }
    if ((values.inventoryQuantity || 0) > 10000000) return "库存量不能大于10000000吨";
    if (
        kind === "consignment" &&
        context.editing &&
        values.inventoryQuantity != null &&
        values.minOrderTon != null &&
        values.inventoryQuantity < (context.reservedQuantity || 0) + values.minOrderTon
    )
        return "库存量不能小于已定吨数加起订吨数";
    if (
        values.floatType === 1 &&
        values.groupFloatRatio != null &&
        (values.groupFloatRatio < 0 || values.groupFloatRatio > 99)
    )
        return "浮动比例为大于等于0，小于100的整数";
    if (values.floatType === 2 && values.groupFloatTon != null) {
        if (values.groupFloatTon > 10000000) return "浮动固定吨数不能大于10000000吨";
        if (values.groupSuccessTon != null && values.groupFloatTon > values.groupSuccessTon)
            return "浮动固定吨数不能大于拼团成功吨数";
    }
    if (kind === "groupPurchase" && values.minAdjustmentTon != null) {
        if (values.minAdjustmentTon === 0) return "最小调整吨数需大于为0";
        if ((values.minAdjustmentTon || 0) > 10000000) return "最小调整吨数不能大于10000000吨";
        if (values.minOrderTon != null && values.minAdjustmentTon > values.minOrderTon)
            return "最小调整吨数不能大于拼团最低吨数";
    }
    if (!values.handlerId) return "请选择商品专员";
    if (!values.waitingPayTime) return "请选择等待付款时间";
    return undefined;
}

/** 旧 address-item 的地址联想控件：选中候选项后一次回填地址、行政区及经纬度。 */
export function ProductAddressAutocomplete({
    ariaLabel,
    regionCode,
    value,
    onChange,
    onSelectSuggestion,
    loadSuggestions = loadOilSiteAddressSuggestions
}: {
    ariaLabel: string;
    regionCode?: string;
    value?: string;
    onChange?: (value: string) => void;
    onSelectSuggestion: (suggestion: OilSiteAddressSuggestion) => void;
    loadSuggestions?: (keyword: string, regionCode?: string) => Promise<OilSiteAddressSuggestion[]>;
}) {
    const [options, setOptions] = useState<
        Array<{ label: string; value: string; suggestion: OilSiteAddressSuggestion }>
    >([]);
    const [loading, setLoading] = useState(false);

    /** Element Plus fetch-suggestions 的 React 等价入口，仅在已选择区域后查询。 */
    const search = async (keyword: string) => {
        onChange?.(keyword);
        if (!regionCode || !keyword.trim()) {
            setOptions([]);
            return;
        }
        setLoading(true);
        try {
            const suggestions = await loadSuggestions(keyword, regionCode);
            setOptions(
                suggestions.map((suggestion, index) => ({
                    label: suggestion.value || suggestion.address || "--",
                    value: suggestion.address || suggestion.value || `候选地址${index + 1}`,
                    suggestion
                }))
            );
        } catch {
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    return (
        <AutoComplete
            aria-label={ariaLabel}
            disabled={!regionCode}
            options={options}
            value={value}
            onChange={onChange}
            onSearch={value => void search(value)}
            onSelect={(_, option) => {
                onChange?.(option.suggestion.address || option.suggestion.value);
                onSelectSuggestion(option.suggestion);
                setOptions([]);
            }}
        >
            <Input
                aria-label={ariaLabel}
                placeholder={regionCode ? "请输入详细地址" : "请先选择区域"}
                suffix={loading ? "加载中" : undefined}
            />
        </AutoComplete>
    );
}

/** 参数编辑器按 Vue multiple-item-product 的双输入框形态维护 JSON 兼容字段。 */
export function ParameterEditor({
    value,
    onChange
}: {
    value?: ParameterItem[] | string;
    onChange?: (value: ParameterItem[]) => void;
}) {
    const items = Array.isArray(value) ? value : parseParameterText(value);
    const update = (next: ParameterItem[]) =>
        onChange?.(next.map((item, index) => ({ ...item, displayOrder: index + 1 })));
    return (
        <div className="consignment-multiple-editor consignment-multiple-editor--parameters">
            {items.map((item, index) => (
                <div className="consignment-multiple-editor__item" key={`${item.displayOrder}-${index}`}>
                    <div className="consignment-multiple-editor__group">
                        <Input
                            aria-label={`参数名称${index + 1}`}
                            value={item.parameterName}
                            onChange={event => {
                                const next = [...items];
                                next[index] = { ...item, parameterName: event.target.value };
                                update(next);
                            }}
                        />
                        <Input
                            aria-label={`参数值${index + 1}`}
                            value={item.parameterValue}
                            onChange={event => {
                                const next = [...items];
                                next[index] = { ...item, parameterValue: event.target.value };
                                update(next);
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        className="consignment-multiple-editor__delete"
                        aria-label={`删除参数值${index + 1}`}
                        onClick={() => update(items.filter((_, itemIndex) => itemIndex !== index))}
                    >
                        ×
                    </button>
                    {index < 9 && index === items.length - 1 ? (
                        <button
                            type="button"
                            className="consignment-multiple-editor__add"
                            aria-label="新增参数值"
                            onClick={() => update([...items, { parameterName: "", parameterValue: "" }])}
                        >
                            +新增参数值
                        </button>
                    ) : null}
                </div>
            ))}
            {items.length === 0 ? (
                <button
                    type="button"
                    className="consignment-multiple-editor__empty-add"
                    aria-label="新增参数值"
                    onClick={() => update([{ parameterName: "", parameterValue: "" }])}
                >
                    +新增参数值
                </button>
            ) : null}
        </div>
    );
}

/** 商品标签直接维护字符串数组，避免把旧 Vue 动态项降级为逗号文本。 */
export function ProductLabelEditor({
    value,
    onChange
}: {
    value?: string[] | string;
    onChange?: (value: string[]) => void;
}) {
    const labels = Array.isArray(value) ? value : value === undefined ? [] : value.split(",");
    const update = (next: string[]) => onChange?.(next);
    return (
        <div className="consignment-multiple-editor">
            {labels.map((label, index) => (
                <div className="consignment-multiple-editor__item" key={index}>
                    <Input
                        aria-label={`商品标签${index + 1}`}
                        placeholder="请输入商品标签"
                        value={label}
                        onChange={event => {
                            const next = [...labels];
                            next[index] = event.target.value;
                            update(next);
                        }}
                    />
                    <button
                        type="button"
                        className="consignment-multiple-editor__delete"
                        aria-label={`删除商品标签${index + 1}`}
                        onClick={() => update(labels.filter((_, itemIndex) => itemIndex !== index))}
                    >
                        ×
                    </button>
                    {index < 4 && index === labels.length - 1 ? (
                        <button
                            type="button"
                            className="consignment-multiple-editor__add"
                            aria-label="新增商品标签"
                            onClick={() => update([...labels, ""])}
                        >
                            +新增商品标签
                        </button>
                    ) : null}
                </div>
            ))}
            {labels.length === 0 ? (
                <button
                    type="button"
                    className="consignment-multiple-editor__empty-add"
                    aria-label="新增商品标签"
                    onClick={() => update([""])}
                >
                    +新增商品标签
                </button>
            ) : null}
        </div>
    );
}

/** 富文本展示使用迁移后的 WangEditor，隐藏 textarea 仅保留既有自动化兼容入口。 */
function RichTextFormItem({
    form,
    name,
    label,
    required = false,
    editorRef
}: {
    form: FormInstance<ConsignmentFormValues>;
    name: keyof ConsignmentFormValues;
    label: string;
    required?: boolean;
    editorRef?: RefObject<RichEditorRef | null>;
}) {
    const value = Form.useWatch(name, form);
    return (
        <div className="consignment-form__rich-row">
            <label
                className={
                    required ? "consignment-form__label consignment-form__label--required" : "consignment-form__label"
                }
            >
                {label}
            </label>
            <div className="consignment-form__rich-content">
                <WangEditor
                    ref={editorRef}
                    value={typeof value === "string" ? value : ""}
                    height="300px"
                    onUpdateValue={next => form.setFieldValue(name, next)}
                />
                <Form.Item name={name} noStyle>
                    <Input.TextArea aria-label={label} className="consignment-form__legacy-control" />
                </Form.Item>
            </div>
        </div>
    );
}

/** 解析 Vue geo 对象使用的经纬度文本。 */
function parseGeoPoint(value?: string): [number, number] | undefined {
    const point = value?.split(",").map(item => Number(item.trim()));
    if (!point || point.length < 2 || point.some(item => !Number.isFinite(item))) return undefined;
    return [point[0], point[1]];
}

/** 复用 Vue 寄售商品详情接口，为编辑表单回填数据。 */
export async function loadConsignment(id: number | string): Promise<ConsignmentRow> {
    const response = await request.service.get<ConsignmentRow>(`/admin/product-consignment/vo/${id}`);
    return response.data;
}

/** 复用 Vue 寄售商品新增草稿和更新草稿接口。 */
export async function saveConsignment(id: number | string | undefined, values: ConsignmentFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/product-consignment/create-save", values);
    } else {
        await request.service.post(`/admin/product-consignment/update-save/${id}`, values);
    }
}

/** 复用 Vue 寄售商品正式提交接口，新增使用 POST，编辑使用 PUT。 */
export async function submitConsignment(id: number | string | undefined, values: ConsignmentFormValues): Promise<void> {
    if (id == null) {
        await request.service.post("/admin/product-consignment", values);
    } else {
        await request.service.put(`/admin/product-consignment/${id}`, values);
    }
}

/** 寄售商品新增/编辑页，覆盖 Vue 表单的核心价格、库存、起订量和介绍字段。 */
export function ConsignmentFormPage({
    loadConsignment: load = loadConsignment,
    saveConsignment: save = saveConsignment,
    submitConsignment: submitForm = submitConsignment,
    loadAddressSuggestions = loadOilSiteAddressSuggestions
}: ConsignmentFormPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const query = new URLSearchParams(location.search);
    const id = query.get("id") || undefined;
    const [form] = Form.useForm<ConsignmentFormValues>();
    const priceType = Form.useWatch("priceType", form);
    const supplierId = Form.useWatch("supplierId", form);
    const productTypeId = Form.useWatch("productTypeId", form);
    const handlerId = Form.useWatch("handlerId", form);
    const settlementMethod = Form.useWatch("settlementMethod", form);
    const depositPaymentMethod = Form.useWatch("depositPaymentMethod", form);
    const waitingPayTime = Form.useWatch("waitingPayTime", form);
    const regionCode = Form.useWatch("regionCode", form);
    const geoLng = Form.useWatch("geoLng", form);
    const geoLat = Form.useWatch("geoLat", form);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const isIncludeTax = Form.useWatch("isIncludeTax", form);
    const payTypes = Form.useWatch("payTypes", form);
    const [imageFiles, setImageFiles] = useState<ProductMediaFile[]>([]);
    const [videoFiles, setVideoFiles] = useState<ProductMediaFile[]>([]);
    const [reservedQuantity, setReservedQuantity] = useState<number>(0);
    const [publishState, setPublishState] = useState<number>();
    const [supplierOptions, setSupplierOptions] = useState<SelectOption[]>([]);
    const [productTypeOptions, setProductTypeOptions] = useState<ProductSelectOption[]>([]);
    const [productTypeTags, setProductTypeTags] = useState<string[]>([]);
    const [offlineAccounts, setOfflineAccounts] = useState<BankAccountValue[]>([{}]);
    const [supplierLoading, setSupplierLoading] = useState(false);
    const [productTypeLoading, setProductTypeLoading] = useState(false);
    const [handlerOptions, setHandlerOptions] = useState<SelectOption[]>([]);
    const [handlerLoading, setHandlerLoading] = useState(false);
    const [mapDialogOpen, setMapDialogOpen] = useState(false);
    const [areaOptions, setAreaOptions] = useState<OilSiteAreaOption[]>([]);
    const [mapData, setMapData] = useState<MapViewForm>({
        id: 0,
        areaCode: [],
        address: "",
        province: "",
        city: "",
        region: "",
        detailAddress: "",
        lngLat: "",
        draggable: true
    });
    const introduceEditorRef = useRef<RichEditorRef>(null);
    const initialEditorFileIdsRef = useRef<number[]>([]);

    const ensureAreaOptions = async () => {
        if (!areaOptions.length) setAreaOptions(await loadOilSiteAreaOptions());
    };

    /** 地址候选携带天地图区划和坐标，按旧 address-item 契约整体回填。 */
    const selectAddressSuggestion = (suggestion: OilSiteAddressSuggestion) => {
        const point = parseGeoPoint(suggestion.lonlat);
        const areaCodes = suggestion.countyCode ? sliceGbCode(suggestion.countyCode) : [];
        form.setFieldsValue({
            address: suggestion.address || suggestion.value,
            ...(areaCodes.length
                ? { provinceCode: areaCodes[0], cityCode: areaCodes[1], regionCode: areaCodes[2] }
                : {}),
            ...(point ? { geoLng: String(point[0]), geoLat: String(point[1]) } : {})
        });
    };

    useEffect(() => {
        if (!id) {
            form.setFieldsValue({
                title: "",
                weight: 0,
                parameterInfo: DEFAULT_PRODUCT_PARAMETERS.map(item => ({ ...item })),
                productLabel: [""],
                handlerMobile: [""],
                payTypes: [1],
                settlementDescn: DEFAULT_SETTLEMENT_DESCN,
                serviceGuarantee: DEFAULT_SERVICE_GUARANTEE,
                regulation: DEFAULT_CONSIGNMENT_REGULATION
            });
            return;
        }

        void load(id)
            .then(result => {
                setErrorMessage(null);
                const values = { ...result.productConsignment } as ConsignmentFormValues;
                const persistedActiveFileIds = (
                    result.productConsignment as ConsignmentFormValues & {
                        activeFileIds?: number[];
                    }
                ).activeFileIds;
                initialEditorFileIdsRef.current = persistedActiveFileIds || [];
                // Vue 接口只返回 payType，编辑时还原为表单使用的多选数组。
                const normalizedPayType = values.payType == null ? undefined : Number(values.payType);
                if (normalizedPayType != null) {
                    values.payType = normalizedPayType;
                    values.payTypes = normalizedPayType === 3 ? [1, 2] : [normalizedPayType];
                }
                if (!Array.isArray(values.handlerMobile)) values.handlerMobile = [""];
                if (!values.parameterInfo?.length)
                    values.parameterInfo = DEFAULT_PRODUCT_PARAMETERS.map(item => ({ ...item }));
                values.productLabel = values.productLabel?.length ? values.productLabel : [""];
                values.introduce ||= "";
                values.deliveryDescn ||= "";
                values.settlementDescn ||= "";
                values.serviceGuarantee ||= "";
                values.regulation ||= "";
                if (values.geo) {
                    values.geoLng = values.geo.lng == null ? "" : String(values.geo.lng);
                    values.geoLat = values.geo.lat == null ? "" : String(values.geo.lat);
                }
                // 编辑回显沿用 Vue 详情中的文件地址；新上传成功后会覆盖对应展示。
                setImageFiles(
                    [result.mainFile, ...((result.otherImgFile || []) as ProductMediaFile[])]
                        .filter((file): file is ProductMediaFile => Boolean(file?.id))
                        .map(file => ({ id: Number(file.id), path: file.path }))
                );
                setVideoFiles(
                    result.videoFile?.id ? [{ id: Number(result.videoFile.id), path: result.videoFile.path }] : []
                );
                setReservedQuantity(
                    Number(
                        (result.productConsignment as ConsignmentFormValues & { orderedTon?: number }).orderedTon || 0
                    )
                );
                setPublishState(result.productConsignment.publishState);
                const accountInfo = (
                    result.productConsignment as ConsignmentFormValues & { accountInfo?: BankAccountValue[] }
                ).accountInfo;
                setOfflineAccounts(accountInfo?.length ? accountInfo : [{}]);
                // Ant Select 必须带当前选项才能显示旧版的名称，而不是后端 ID。
                const supplierName =
                    result.productConsignment.supplierEnterprise?.supplierName ||
                    result.productConsignment.supplierEnterprise?.enterpriseName ||
                    result.productConsignment.supplierEnterprise?.name;
                if (values.supplierId != null && supplierName) {
                    setSupplierOptions([{ value: values.supplierId, label: supplierName }]);
                }
                const productTypeName = result.productType?.areaAbbreviation
                    ? `${result.productType.name || ""} | ${result.productType.areaAbbreviation}`
                    : result.productType?.name;
                if (values.productTypeId && productTypeName) {
                    const tags = result.productTypeVo?.tags?.map(tag => tag.name || "").filter(Boolean) || [];
                    setProductTypeTags(tags);
                    setProductTypeOptions([{ value: values.productTypeId, label: productTypeName, tags }]);
                }
                if (values.handlerId != null && result.productConsignment.handlerName) {
                    setHandlerOptions([{ value: values.handlerId, label: result.productConsignment.handlerName }]);
                }
                form.setFieldsValue(values);
            })
            .catch(() => setErrorMessage("寄售商品详情加载失败"));
    }, [form, id, load]);

    /** 远程搜索寄售供应商，行为与 Vue el-select remote-method 一致。 */
    const searchSuppliers = async (name = "") => {
        setSupplierLoading(true);
        try {
            const options = await enterpriseManageService.selector({ supplierName: name });
            setSupplierOptions(options.map(item => ({ value: item.id, label: item.supplierName || "--" })));
        } catch {
            // 远程搜索失败时保留当前回显选项，避免编辑页退回显示原始 ID。
        } finally {
            setSupplierLoading(false);
        }
    };

    /** 远程搜索寄售品类，并使用“名称 | 区域简称”还原 Vue 选项文案。 */
    const searchProductTypes = async (keyword = "") => {
        setProductTypeLoading(true);
        try {
            const options = await prodTypeService.selectorVo(1, keyword);
            setProductTypeOptions(
                options.map(item => ({
                    value: item.productType.id,
                    label: item.productType.areaAbbreviation
                        ? `${item.productType.name} | ${item.productType.areaAbbreviation}`
                        : item.productType.name,
                    specificationModulus: item.productType.specificationModulus,
                    tags: item.tags.map(tag => tag.name)
                }))
            );
        } catch {
            // 远程搜索失败时保留当前回显选项，避免编辑页退回显示原始 ID。
        } finally {
            setProductTypeLoading(false);
        }
    };

    /** 远程搜索商品专员，选项文案与 Vue 的“姓名 | 工号”保持一致。 */
    const searchHandlers = async (keyword = "") => {
        setHandlerLoading(true);
        try {
            const options = await consignmentService.findServiceSpecials(keyword);
            setHandlerOptions(
                options.map(item => ({
                    value: item.user.personId,
                    label: item.user.employeeId ? `${item.user.name} | ${item.user.employeeId}` : item.user.name
                }))
            );
        } catch {
            // 搜索失败时保留编辑页当前专员，避免 Select 退回显示原始 ID。
        } finally {
            setHandlerLoading(false);
        }
    };

    /** 按 Vue upload-list 契约上传媒体，并把返回的文件 ID 写回原表单字段。 */
    const uploadMedia = async (
        event: ChangeEvent<HTMLInputElement>,
        mediaType: "image" | "video",
        replaceIndex?: number
    ) => {
        const selectedFiles = Array.from(event.target.files || []);
        event.target.value = "";
        if (!selectedFiles.length) return;

        const allowedTypes = mediaType === "image" ? CONSIGNMENT_IMAGE_TYPES : CONSIGNMENT_VIDEO_TYPES;
        const maxSize = mediaType === "image" ? CONSIGNMENT_IMAGE_MAX_SIZE : CONSIGNMENT_VIDEO_MAX_SIZE;
        const fieldName = mediaType === "image" ? "imgMainId" : "videoId";
        const label = mediaType === "image" ? "主图" : "视频";
        if (selectedFiles.some(file => !allowedTypes.includes(file.type) || file.size > maxSize)) {
            setErrorMessage(
                mediaType === "image"
                    ? "主图仅支持 JPEG/PNG，且大小不能超过 2MB"
                    : "视频仅支持 MP4/MOV/MKV，且大小不能超过 200MB"
            );
            return;
        }

        try {
            const { fileService } = await import("@/api/services/fileService");
            const remaining = replaceIndex == null ? (mediaType === "image" ? 6 - imageFiles.length : 1) : 1;
            const uploadedFiles: ProductMediaFile[] = [];
            for (const file of selectedFiles.slice(0, remaining)) {
                const formData = new FormData();
                formData.append("file", file);
                const uploadedFile = (await fileService.uploadFile(formData)) as ConsignmentUploadedFile;
                const fileId = Number(uploadedFile.id);
                if (!Number.isFinite(fileId) || fileId <= 0) throw new Error("上传响应缺少文件 ID");
                uploadedFiles.push({ id: fileId, path: uploadedFile.path || file.name });
            }
            const currentFiles = mediaType === "image" ? imageFiles : videoFiles;
            const nextFiles =
                replaceIndex == null
                    ? mediaType === "image"
                        ? [...imageFiles, ...uploadedFiles]
                        : uploadedFiles
                    : currentFiles.map((file, index) =>
                          uploadedFiles[0] && index === replaceIndex ? uploadedFiles[0] : file
                      );
            form.setFieldValue(fieldName, nextFiles[0]?.id);
            if (mediaType === "image") setImageFiles(nextFiles);
            else setVideoFiles(nextFiles);
            setErrorMessage(null);
        } catch {
            setErrorMessage(`寄售商品${label}上传失败`);
        }
    };

    /** 第一张图片是接口主图；“设置主图”通过重排保持 imgMainId/imgIds 同步。 */
    const setMainImage = (index: number) => {
        setImageFiles(current => {
            const selected = current[index];
            if (!selected) return current;
            const next = [selected, ...current.filter((_, itemIndex) => itemIndex !== index)];
            form.setFieldValue("imgMainId", selected.id);
            return next;
        });
    };

    /** 打开 Vue address-item 对应的地图选择弹窗，并回显已有 geo 坐标。 */
    const openMapPicker = () => {
        const currentPoint = parseGeoPoint(
            [form.getFieldValue("geoLng"), form.getFieldValue("geoLat")].filter(value => value !== "").join(",")
        );
        setMapData({
            id: Number(id) || 0,
            areaCode: [
                form.getFieldValue("provinceCode"),
                form.getFieldValue("cityCode"),
                form.getFieldValue("regionCode")
            ].filter(Boolean),
            address: form.getFieldValue("address") || "",
            province: "",
            city: "",
            region: "",
            detailAddress: form.getFieldValue("address") || "",
            lngLat: currentPoint ? `${currentPoint[0]},${currentPoint[1]}` : "",
            draggable: true
        });
        setMapDialogOpen(true);
    };

    /** 地图确认必须整体回填省市区、详细地址和坐标，保持旧 address-item 的单次提交链路。 */
    const confirmMapPicker = (data: MapViewForm) => {
        const point = parseGeoPoint(data.lngLat);
        if (!point) {
            setErrorMessage("请选择有效经纬度");
            return;
        }
        form.setFieldsValue({
            provinceCode: data.areaCode?.[0],
            cityCode: data.areaCode?.[1],
            regionCode: data.areaCode?.[2],
            address: data.detailAddress || data.address || "",
            geoLng: String(point[0]),
            geoLat: String(point[1])
        });
        setMapDialogOpen(false);
    };

    const submit = async (isDraft: boolean) => {
        try {
            // Vue 草稿允许不完整字段，正式提交才执行表单必填校验。
            const values = isDraft ? form.getFieldsValue() : await form.validateFields();
            const contractError = validateLegacyProductDraft(values, "consignment", {
                draft: isDraft,
                editing: Boolean(id),
                reservedQuantity
            });
            if (contractError) {
                setErrorMessage(contractError);
                return;
            }
            const mobiles = values.handlerMobile || [];
            if (!isDraft && (!mobiles.length || mobiles.some(mobile => !/^[0-9-]{1,18}$/.test(mobile)))) {
                setErrorMessage(mobiles.some(Boolean) ? "请输入正确的手机号" : "请输入商品专员联系电话");
                return;
            }
            if (
                values.parameterInfo?.some(
                    item => (item.parameterName?.length || 0) > 10 || (item.parameterValue?.length || 0) > 10
                )
            ) {
                setErrorMessage("参数名称和参数值不能超过10个字符");
                return;
            }
            if (!isDraft && values.parameterInfo?.some(item => !item.parameterName || !item.parameterValue)) {
                setErrorMessage("请输入参数值");
                return;
            }
            if (values.productLabel?.some(label => label.length > 6)) {
                setErrorMessage("商品标签不能超过6个字符");
                return;
            }
            if (!isDraft && values.productLabel?.some(label => !label)) {
                setErrorMessage("请输入商品标签");
                return;
            }
            if (!isDraft) {
                const requiredFields: Array<[unknown, string]> = [
                    [values.priceType, "请选择单价类型"],
                    [values.unitPrice, "请输入单价"],
                    [imageFiles.length, "请上传图片"],
                    [videoFiles.length, "请上传视频"],
                    [values.supplierId, "请选择供应商"],
                    [values.productTypeId, "请选择品类"],
                    [values.productTypeSpec, "请输入规格"],
                    [values.introduce, "请输入商品详情"],
                    [values.deliveryStartTime && values.deliveryEndTime, "请选择提货周期"],
                    [values.regionCode, "请选择交付地点"],
                    [values.address, "请输入详细地址"],
                    [values.geoLng && values.geoLat, "请选择经纬度"],
                    [values.inventoryQuantity, "请输入库存量"],
                    [values.minOrderTon, "请输入起订吨数"],
                    [values.minAdjustmentTon, "请输入最小调整吨数"],
                    [values.settlementMethod, "请选择结算方式"],
                    [values.payTypes?.length, "请选择支付方式"],
                    [values.regulation, "请输入寄售规则"],
                    [values.handlerId, "请选择商品专员"],
                    [values.waitingPayTime, "请选择等待付款时间"]
                ];
                const missing = requiredFields.find(
                    ([value]) => value === undefined || value === null || value === "" || value === 0
                );
                if (missing) {
                    setErrorMessage(missing[1]);
                    return;
                }
            }
            if (!isDraft && values.isIncludeTax === 1 && !values.taxRate) {
                setErrorMessage("请选择税率");
                return;
            }
            if (
                !isDraft &&
                values.inventoryQuantity &&
                values.minOrderTon &&
                values.inventoryQuantity < values.minOrderTon
            ) {
                setErrorMessage("起订吨数不能大于库存量");
                return;
            }
            if (
                !isDraft &&
                values.minOrderTon &&
                values.minAdjustmentTon &&
                values.minAdjustmentTon > values.minOrderTon
            ) {
                setErrorMessage("最小调整吨数不能大于起订吨数");
                return;
            }
            if (
                !isDraft &&
                id &&
                values.inventoryQuantity &&
                values.minOrderTon &&
                values.inventoryQuantity < reservedQuantity + values.minOrderTon
            ) {
                setErrorMessage("库存量不能小于已定吨数加起订吨数");
                return;
            }
            if (!isDraft && values.payTypes?.includes(2) && offlineAccounts.some(account => !account.account)) {
                setErrorMessage("请选择收款账户");
                return;
            }
            // Ant Form 会保留已挂载但未填写的 undefined 字段，提交前按 Vue model 语义移除。
            const payload = Object.fromEntries(
                Object.entries(values).filter(([, value]) => value !== undefined)
            ) as ConsignmentFormValues & {
                imgIds?: number[];
                activeFileIds?: number[];
                platformBankAccountForm?: BankAccountValue[];
            };
            payload.imgMainId = imageFiles[0]?.id ?? payload.imgMainId;
            payload.imgIds = imageFiles.slice(1).map(file => file.id);
            payload.videoId = videoFiles[0]?.id ?? payload.videoId;
            const editorIds = [
                ...extractProductEditorFileIds(payload.introduce),
                ...(introduceEditorRef.current
                    ? ["image", "video"].flatMap(type =>
                          introduceEditorRef
                              .current!.editor.getElemsByType(type as "image" | "video")
                              .map(item => introduceEditorRef.current!.files.get(item.src))
                              .filter((fileId): fileId is number => Boolean(fileId))
                      )
                    : [])
            ];
            // 历史详情若仅返回 activeFileIds 而正文 URL 无 #id，旧端只能按该清单保留关联。
            if (!editorIds.length && payload.introduce) editorIds.push(...initialEditorFileIdsRef.current);
            // activeFileIds 属于 WangEditor 正文媒体，商品主图/视频由独立字段维护，不能混入。
            payload.activeFileIds = Array.from(new Set(editorIds));
            // 原生 select 返回字符串，转换为 Vue 接口要求的数值价格类型。
            const rawPriceType = payload.priceType as unknown;
            if (rawPriceType !== undefined && rawPriceType !== "") payload.priceType = Number(rawPriceType);
            const rawWaitingPayTime = payload.waitingPayTime as unknown;
            if (rawWaitingPayTime !== undefined && rawWaitingPayTime !== "") {
                payload.waitingPayTime = Number(rawWaitingPayTime);
            }
            // 原生输入框返回字符串，转换为 Vue 接口要求的媒体文件 ID 数值。
            const rawImgMainId = payload.imgMainId as unknown;
            if (rawImgMainId !== undefined && rawImgMainId !== "") {
                payload.imgMainId = Number(rawImgMainId);
            }
            const rawVideoId = payload.videoId as unknown;
            if (rawVideoId !== undefined && rawVideoId !== "") {
                payload.videoId = Number(rawVideoId);
            }
            // 原生输入框返回字符串，转换为 Vue 接口要求的最小调整吨数数值。
            const rawMinAdjustmentTon = payload.minAdjustmentTon as unknown;
            if (rawMinAdjustmentTon !== undefined && rawMinAdjustmentTon !== "") {
                payload.minAdjustmentTon = Number(rawMinAdjustmentTon);
            }
            // 原生 select/input 返回字符串，转换为 Vue 接口要求的结算数值字段。
            const rawSettlementMethod = payload.settlementMethod as unknown;
            if (rawSettlementMethod !== undefined && rawSettlementMethod !== "") {
                payload.settlementMethod = Number(rawSettlementMethod);
            }
            const rawDepositPaymentMethod = payload.depositPaymentMethod as unknown;
            if (rawDepositPaymentMethod !== undefined && rawDepositPaymentMethod !== "") {
                payload.depositPaymentMethod = Number(rawDepositPaymentMethod);
            }
            const rawPaymentRatio = payload.paymentRatio as unknown;
            if (rawPaymentRatio !== undefined && rawPaymentRatio !== "") {
                payload.paymentRatio = Number(rawPaymentRatio);
            }
            const rawFixedAmount = payload.fixedAmount as unknown;
            if (rawFixedAmount !== undefined && rawFixedAmount !== "") {
                payload.fixedAmount = Number(rawFixedAmount);
            }
            // 原生输入框返回字符串，转换为 Vue 接口要求的供应商 ID 数值。
            const rawSupplierId = payload.supplierId as unknown;
            if (rawSupplierId !== undefined && rawSupplierId !== "") {
                payload.supplierId = Number(rawSupplierId);
            }
            const rawHandlerId = payload.handlerId as unknown;
            if (rawHandlerId !== undefined && rawHandlerId !== "") {
                payload.handlerId = Number(rawHandlerId);
            }
            // 地图选择器回填的经纬度在提交前还原为 Vue geo 对象。
            if (payload.geoLng !== undefined || payload.geoLat !== undefined) {
                const lng = payload.geoLng === "" || payload.geoLng === undefined ? undefined : Number(payload.geoLng);
                const lat = payload.geoLat === "" || payload.geoLat === undefined ? undefined : Number(payload.geoLat);
                payload.geo = { lng, lat };
                delete payload.geoLng;
                delete payload.geoLat;
            }
            // Vue 后端接收单值 payType：线上=1、线下=2、线上线下=3。
            const rawPayTypes = payload.payTypes as unknown;
            if (Array.isArray(rawPayTypes)) {
                const payTypes = rawPayTypes.map(item => Number(item));
                payload.payTypes = payTypes;
                payload.payType = payTypes.includes(1) && payTypes.includes(2) ? 3 : payTypes[0];
                payload.platformBankAccountForm = payTypes.includes(2) ? offlineAccounts : undefined;
            }
            // 原生 select 返回字符串，转换为 Vue 接口要求的数值税费类型。
            const rawIsIncludeTax = payload.isIncludeTax as unknown;
            if (rawIsIncludeTax !== undefined && rawIsIncludeTax !== "") {
                payload.isIncludeTax = Number(rawIsIncludeTax);
            }
            const rawTaxRate = payload.taxRate as unknown;
            if (rawTaxRate !== undefined && rawTaxRate !== "") {
                payload.taxRate = Number(rawTaxRate);
            }
            // Vue 保存与提交共用表单数据，但分别命中草稿和正式提交接口。
            await (isDraft ? save : submitForm)(id, payload);
            void navigate("/productManagement/consignment");
        } catch {
            setErrorMessage("寄售商品保存失败");
        }
    };

    return (
        <section className="consignment-form-page" aria-label="consignment-form-page">
            <section className="section-card consignment-form__header">
                <ZhDetailHeader title={id ? "编辑寄售商品" : "新增寄售商品"} />
            </section>
            {errorMessage ? (
                <p className="consignment-form__alert" role="alert">
                    {errorMessage}
                </p>
            ) : null}
            <Form
                form={form}
                className="consignment-form__form"
                colon={false}
                labelAlign="right"
                labelCol={{ flex: "128px" }}
                wrapperCol={{ flex: 1 }}
            >
                <FormSection title="商品信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item
                                label="商品标题"
                                name="title"
                                rules={[{ required: true, message: "请输入商品标题" }]}
                            >
                                <Input placeholder="请输入商品名称" allowClear />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="权重值" name="weight">
                                <InputNumber className="w-full" min={0} controls={false} placeholder="请输入权重值" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={2}>
                            <div className="consignment-form__price-row">
                                <label className="consignment-form__label consignment-form__label--required">
                                    销售单价
                                </label>
                                <div className="consignment-form__price-control">
                                    <Form.Item name="priceType" noStyle>
                                        <Input aria-label="单价类型" className="consignment-form__legacy-control" />
                                    </Form.Item>
                                    <Radio.Group
                                        value={Number(priceType) || undefined}
                                        onChange={event => form.setFieldValue("priceType", event.target.value)}
                                    >
                                        <Radio value={1}>
                                            <span className="consignment-form__price-radio">
                                                固定价格
                                                <PriceTypeTip type={1} />
                                            </span>
                                        </Radio>
                                        <Radio value={2}>
                                            <span className="consignment-form__price-radio">
                                                预估价格
                                                <PriceTypeTip type={2} />
                                            </span>
                                        </Radio>
                                    </Radio.Group>
                                    <Form.Item name="unitPrice" noStyle>
                                        <InputNumber
                                            min={0}
                                            controls={false}
                                            placeholder="请输入销售单价"
                                            addonAfter="元/吨"
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="是否含税" required>
                                <div className="consignment-form__tax-control">
                                    <Form.Item name="isIncludeTax" noStyle>
                                        <Select
                                            aria-label="是否含税选择"
                                            options={[
                                                { label: "不含", value: 0 },
                                                { label: "含税", value: 1 }
                                            ]}
                                            onChange={value => {
                                                form.setFieldValue("isIncludeTax", value);
                                                if (value !== 1) form.setFieldValue("taxRate", undefined);
                                            }}
                                        />
                                    </Form.Item>
                                    <Input
                                        aria-label="是否含税"
                                        className="consignment-form__legacy-control"
                                        value={isIncludeTax ?? ""}
                                        onChange={event => form.setFieldValue("isIncludeTax", event.target.value)}
                                    />
                                    {Number(isIncludeTax) === 1 ? (
                                        <Form.Item name="taxRate" noStyle>
                                            <Select
                                                aria-label="税率选择"
                                                options={[3, 6, 9, 13].map((label, index) => ({
                                                    label: `${label}%`,
                                                    value: index + 1
                                                }))}
                                            />
                                        </Form.Item>
                                    ) : null}
                                    <Input
                                        aria-label="税率"
                                        className="consignment-form__legacy-control"
                                        value={form.getFieldValue("taxRate") ?? ""}
                                        onChange={event => form.setFieldValue("taxRate", event.target.value)}
                                    />
                                </div>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="等待付款时间" required>
                                <Select
                                    aria-label="等待付款时间选择"
                                    value={waitingPayTime}
                                    options={[
                                        "3小时",
                                        "12小时",
                                        "24小时",
                                        "48小时",
                                        "72小时",
                                        "5天",
                                        "7天",
                                        "不限时长"
                                    ].map((label, index) => ({ label, value: index + 1 }))}
                                    onChange={value => form.setFieldValue("waitingPayTime", value)}
                                />
                                <Form.Item name="waitingPayTime" noStyle>
                                    <Input aria-label="等待付款时间" className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <div className="consignment-form__media-row">
                                <label className="consignment-form__label consignment-form__label--required">
                                    图片
                                </label>
                                <ProductMediaUpload
                                    type="image"
                                    files={imageFiles}
                                    inputIdPrefix="consignment"
                                    onUpload={(event, replaceIndex) => void uploadMedia(event, "image", replaceIndex)}
                                    onSetMain={setMainImage}
                                    onRemove={index => {
                                        const next = imageFiles.filter((_, fileIndex) => fileIndex !== index);
                                        setImageFiles(next);
                                        form.setFieldValue("imgMainId", next[0]?.id);
                                    }}
                                />
                                <Form.Item name="imgMainId" noStyle>
                                    <Input aria-label="主图文件 ID" className="consignment-form__legacy-control" />
                                </Form.Item>
                            </div>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <div className="consignment-form__media-row">
                                <label className="consignment-form__label consignment-form__label--required">
                                    视频
                                </label>
                                <ProductMediaUpload
                                    type="video"
                                    files={videoFiles}
                                    inputIdPrefix="consignment"
                                    onUpload={(event, replaceIndex) => void uploadMedia(event, "video", replaceIndex)}
                                    onRemove={() => {
                                        setVideoFiles([]);
                                        form.setFieldValue("videoId", undefined);
                                    }}
                                />
                                <Form.Item name="videoId" noStyle>
                                    <Input aria-label="视频文件 ID" className="consignment-form__legacy-control" />
                                </Form.Item>
                            </div>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="供应商" required>
                                <Select
                                    allowClear
                                    showSearch
                                    filterOption={false}
                                    loading={supplierLoading}
                                    options={supplierOptions}
                                    placeholder="请选择供应商"
                                    value={supplierId}
                                    onChange={value => form.setFieldValue("supplierId", value)}
                                    onSearch={value => void searchSuppliers(value)}
                                />
                                <Form.Item name="supplierId" noStyle>
                                    <Input aria-label="供应商 ID" className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="品类" required>
                                <Select
                                    allowClear
                                    showSearch
                                    filterOption={false}
                                    loading={productTypeLoading}
                                    options={productTypeOptions}
                                    placeholder="请选择砂石品类"
                                    value={productTypeId}
                                    onChange={value => {
                                        form.setFieldValue("productTypeId", value);
                                        const selected = productTypeOptions.find(option => option.value === value);
                                        setProductTypeTags(selected?.tags || []);
                                        if (selected?.specificationModulus)
                                            form.setFieldValue("productTypeSpec", selected.specificationModulus);
                                    }}
                                    onSearch={value => void searchProductTypes(value)}
                                />
                                <Form.Item name="productTypeId" noStyle>
                                    <Input aria-label="品类 ID" className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="规格" name="productTypeSpec" required>
                                <Input placeholder="请输入规格" allowClear />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="标签">
                                <Input
                                    aria-label="品类标签"
                                    disabled
                                    value={productTypeTags.length ? productTypeTags.join(",") : "--"}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="参数值" name="parameterInfo">
                                <ParameterEditor />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <Form.Item label="商品标签" name="productLabel">
                                <ProductLabelEditor />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <RichTextFormItem
                                form={form}
                                name="introduce"
                                label="商品介绍"
                                required
                                editorRef={introduceEditorRef}
                            />
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>

                <FormSection title="提货信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item label="提货周期" required>
                                <Space.Compact className="w-full">
                                    <Form.Item name="deliveryStartTime" noStyle>
                                        <ZhDatePicker
                                            aria-label="提货开始时间"
                                            className="w-full"
                                            disabled={Boolean(id && Number(publishState) !== 6)}
                                            disabledDate={date => date.getTime() < new Date().setHours(0, 0, 0, 0)}
                                            format="YYYY-MM-DD"
                                            placeholder="请选择提货开始日期"
                                            type="date"
                                            valueFormat="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                    <Form.Item name="deliveryEndTime" noStyle>
                                        <ZhDatePicker
                                            aria-label="提货结束时间"
                                            className="w-full"
                                            disabledDate={date => date.getTime() < new Date().setHours(0, 0, 0, 0)}
                                            format="YYYY-MM-DD"
                                            placeholder="请选择提货结束日期"
                                            type="date"
                                            valueFormat="YYYY-MM-DD"
                                        />
                                    </Form.Item>
                                </Space.Compact>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="提货地点">
                                <Cascader
                                    aria-label="交付地点"
                                    className="w-full"
                                    options={areaOptions}
                                    fieldNames={{ label: "name", value: "id", children: "childs" }}
                                    value={[
                                        form.getFieldValue("provinceCode"),
                                        form.getFieldValue("cityCode"),
                                        form.getFieldValue("regionCode")
                                    ].filter(Boolean)}
                                    onFocus={() => void ensureAreaOptions()}
                                    onChange={value =>
                                        form.setFieldsValue({
                                            provinceCode: value[0] == null ? undefined : String(value[0]),
                                            cityCode: value[1] == null ? undefined : String(value[1]),
                                            regionCode: value[2] == null ? undefined : String(value[2])
                                        })
                                    }
                                />
                                {(["provinceCode", "cityCode", "regionCode"] as const).map((field, index) => (
                                    <Form.Item name={field} noStyle key={field}>
                                        <Input
                                            aria-label={["省编码", "市编码", "区编码"][index]}
                                            className="consignment-form__legacy-control"
                                        />
                                    </Form.Item>
                                ))}
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item>
                                <Form.Item name="address" noStyle>
                                    <ProductAddressAutocomplete
                                        ariaLabel="提货地址"
                                        regionCode={regionCode}
                                        loadSuggestions={loadAddressSuggestions}
                                        onSelectSuggestion={selectAddressSuggestion}
                                    />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="经纬度">
                                <button
                                    aria-label="经纬度"
                                    className="w-full rounded border border-slate-200 px-3 py-1.5 text-left disabled:cursor-not-allowed disabled:text-slate-400"
                                    disabled={!regionCode}
                                    type="button"
                                    onClick={openMapPicker}
                                >
                                    {geoLng && geoLat
                                        ? `${geoLng}, ${geoLat}`
                                        : regionCode
                                          ? "请选择经纬度"
                                          : "请先选择区域"}
                                </button>
                                <Form.Item name="geoLng" noStyle>
                                    <input aria-label="经度" type="hidden" />
                                </Form.Item>
                                <Form.Item name="geoLat" noStyle>
                                    <input aria-label="纬度" type="hidden" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="库存量" name="inventoryQuantity">
                                <InputNumber className="w-full" min={0} controls={false} addonAfter="吨" />
                            </Form.Item>
                        </ZhGridItem>
                        {id ? (
                            <ZhGridItem>
                                <Form.Item label="已定吨数">
                                    <InputNumber
                                        aria-label="已定吨数"
                                        className="w-full"
                                        disabled
                                        addonAfter="吨"
                                        value={reservedQuantity}
                                    />
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        <ZhGridItem>
                            <Form.Item label="起订吨数" name="minOrderTon">
                                <InputNumber className="w-full" min={0} controls={false} addonAfter="吨" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="最小调整吨数" name="minAdjustmentTon">
                                <Input aria-label="最小调整吨数" suffix="吨" />
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={4}>
                            <RichTextFormItem form={form} name="deliveryDescn" label="提货说明" />
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>

                <FormSection title="结算信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item label="下单要求">
                                <Radio.Group
                                    aria-label="下单要求选择"
                                    options={[
                                        { label: "全部货款", value: 1 },
                                        { label: "支付定金", value: 2 }
                                    ]}
                                    value={settlementMethod}
                                    onChange={event =>
                                        form.setFieldsValue({
                                            settlementMethod: event.target.value,
                                            depositPaymentMethod: undefined,
                                            paymentRatio: undefined,
                                            fixedAmount: undefined
                                        })
                                    }
                                />
                                <Form.Item name="settlementMethod" noStyle>
                                    <Input aria-label="下单要求" className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem>
                            <Form.Item label="支付方式" name="payTypes">
                                <Checkbox.Group
                                    aria-label="支付方式"
                                    options={[
                                        { label: "线上支付", value: 1 },
                                        { label: "线下支付", value: 2 }
                                    ]}
                                />
                            </Form.Item>
                        </ZhGridItem>
                        {Number(settlementMethod) === 2 ? (
                            <ZhGridItem>
                                <Form.Item label="支付定金方式">
                                    <Select
                                        aria-label="支付定金方式选择"
                                        options={[
                                            { label: "货款比例", value: 1 },
                                            { label: "固定金额", value: 2 }
                                        ]}
                                        value={depositPaymentMethod}
                                        onChange={value =>
                                            form.setFieldsValue({
                                                depositPaymentMethod: value,
                                                paymentRatio: undefined,
                                                fixedAmount: undefined
                                            })
                                        }
                                    />
                                    <Form.Item name="depositPaymentMethod" noStyle>
                                        <Input aria-label="支付定金方式" className="consignment-form__legacy-control" />
                                    </Form.Item>
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        {payTypes?.includes(2) ? (
                            <ZhGridItem span={4}>
                                <OfflineAccountEditor value={offlineAccounts} onChange={setOfflineAccounts} />
                            </ZhGridItem>
                        ) : null}
                        {Number(settlementMethod) === 2 && Number(depositPaymentMethod) === 1 ? (
                            <ZhGridItem>
                                <Form.Item label="货款比例" name="paymentRatio">
                                    <Input aria-label="货款比例" suffix="%" />
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        {Number(settlementMethod) === 2 && Number(depositPaymentMethod) === 2 ? (
                            <ZhGridItem>
                                <Form.Item label="固定金额" name="fixedAmount">
                                    <Input aria-label="固定金额" suffix="元" />
                                </Form.Item>
                            </ZhGridItem>
                        ) : null}
                        <ZhGridItem span={4}>
                            <RichTextFormItem form={form} name="settlementDescn" label="结算说明" />
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>

                <FormSection title="服务保障">
                    <RichTextFormItem form={form} name="serviceGuarantee" label="服务保障" />
                    <RichTextFormItem form={form} name="regulation" label="寄售规则" />
                </FormSection>

                <FormSection title="其他信息">
                    <ZhGrid cols={4} gap={[16, 0]}>
                        <ZhGridItem>
                            <Form.Item label="商品专员" required>
                                <Select
                                    aria-label="商品专员"
                                    allowClear
                                    showSearch
                                    filterOption={false}
                                    loading={handlerLoading}
                                    disabled={Boolean(id && Number(publishState) !== 6)}
                                    options={handlerOptions}
                                    placeholder="请选择商品专员"
                                    value={handlerId}
                                    onChange={value => form.setFieldValue("handlerId", value)}
                                    onSearch={value => void searchHandlers(value)}
                                />
                                <Form.Item name="handlerId" noStyle>
                                    <Input aria-label="专员 ID" className="consignment-form__legacy-control" />
                                </Form.Item>
                            </Form.Item>
                        </ZhGridItem>
                        <ZhGridItem span={3}>
                            <Form.Item
                                label="商品专员联系电话"
                                name="handlerMobile"
                                rules={[
                                    {
                                        validator: (_, value: string[] | undefined) =>
                                            !value?.length || value.some(item => !/^[0-9-]{1,18}$/.test(item))
                                                ? Promise.reject(new Error("请输入正确的手机号"))
                                                : Promise.resolve()
                                    }
                                ]}
                            >
                                <HandlerMobileEditor />
                            </Form.Item>
                        </ZhGridItem>
                    </ZhGrid>
                </FormSection>
            </Form>
            <Space className="operation-fixed-btn consignment-form__actions" data-testid="consignment-form-actions">
                <Button aria-label="保存寄售商品" type="primary" onClick={() => void submit(true)}>
                    保存
                </Button>
                <Button aria-label="提交寄售商品" type="primary" onClick={() => void submit(false)}>
                    提交
                </Button>
                <Button onClick={() => void navigate("/productManagement/consignment")}>返回</Button>
            </Space>
            {mapDialogOpen ? (
                <ZhMapDialog
                    data={mapData}
                    formType={id ? FormTypeEnum.Edit : FormTypeEnum.Add}
                    pointerMarker
                    visible={mapDialogOpen}
                    onSubmit={confirmMapPicker}
                    onUpdateData={setMapData}
                    onUpdateVisible={setMapDialogOpen}
                />
            ) : null}
        </section>
    );
}

export default ConsignmentFormPage;
